import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker, DateRange } from "react-day-picker";
import { de } from "date-fns/locale";
import { format, parseISO, startOfToday } from "date-fns";
import "react-day-picker/style.css";

interface DateRangePickerProps {
  /** Start of the range as an ISO date string (yyyy-MM-dd) or "" when unset. */
  startDate: string;
  /** End of the range as an ISO date string (yyyy-MM-dd) or "" when unset. */
  endDate: string;
  onChange: (range: { startDate: string; endDate: string }) => void;
  /** When true, dates before today cannot be selected (employee requests). */
  restrictPast?: boolean;
}

const PAST_YEARS = 3;
const FUTURE_YEARS = 2;
const VIEWPORT_MARGIN = 8;
const GAP_BELOW_TRIGGER = 4;

const toDate = (iso: string): Date | undefined => (iso ? parseISO(iso) : undefined);
const toIso = (date: Date | undefined): string => (date ? format(date, "yyyy-MM-dd") : "");
const toDisplay = (iso: string): string => (iso ? format(parseISO(iso), "dd.MM.yyyy") : "");

/**
 * Calendar-based range picker for vacation entries. The calendar opens in a portal on
 * top of everything (fixed positioning anchored to the trigger), so it is never clipped
 * by the surrounding scroll container. Month/year dropdowns allow jumping to any date and
 * the whole range is picked in two clicks.
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  restrictPast = false,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Counts day clicks within the current open session so the calendar stays open
  // for picking both the start and the end date (react-day-picker reports a complete
  // one-day range already on the first click).
  const clickCountRef = useRef(0);

  const toggleOpen = () => {
    setOpen((value) => {
      const next = !value;
      if (next) {
        clickCountRef.current = 0;
      }
      return next;
    });
  };

  // Keep the floating calendar anchored to the trigger, even while scrolling/resizing.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    const reposition = () => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) {
        return;
      }
      const anchor = trigger.getBoundingClientRect();
      const floating = popover.getBoundingClientRect();

      let left = anchor.left;
      if (left + floating.width > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - floating.width - VIEWPORT_MARGIN);
      }

      let top = anchor.bottom + GAP_BELOW_TRIGGER;
      const overflowsBottom = top + floating.height > window.innerHeight - VIEWPORT_MARGIN;
      const fitsAbove = anchor.top - floating.height - GAP_BELOW_TRIGGER >= VIEWPORT_MARGIN;
      if (overflowsBottom && fitsAbove) {
        top = anchor.top - floating.height - GAP_BELOW_TRIGGER;
      }

      setCoords({ top, left });
    };

    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Close when clicking outside both the trigger and the (portalled) calendar.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const today = startOfToday();
  const selected: DateRange | undefined = startDate
    ? { from: toDate(startDate), to: toDate(endDate) }
    : undefined;

  const handleSelect = (range: DateRange | undefined, triggerDate: Date) => {
    clickCountRef.current += 1;
    // The first click of a session always starts a fresh range at the clicked day,
    // so picking start then end works the same whether the field was empty or pre-filled.
    if (clickCountRef.current === 1) {
      onChange({ startDate: toIso(triggerDate), endDate: "" });
      return;
    }
    // The second click sets the end and closes the calendar.
    onChange({ startDate: toIso(range?.from), endDate: toIso(range?.to) });
    setOpen(false);
  };

  const label = startDate
    ? `${toDisplay(startDate)} – ${toDisplay(endDate || startDate)}`
    : "Zeitraum wählen";

  return (
    <div ref={triggerRef} style={{ display: "inline-block" }}>
      <button type="button" onClick={toggleOpen} style={triggerStyle}>
        <span role="img" aria-label="Kalender">📅</span> {label}
      </button>
      {open &&
        createPortal(
          <div ref={popoverRef} style={{ ...popoverStyle, top: coords.top, left: coords.left }}>
            <DayPicker
              mode="range"
              required
              numberOfMonths={2}
              captionLayout="dropdown"
              weekStartsOn={1}
              locale={de}
              defaultMonth={toDate(startDate) ?? today}
              startMonth={restrictPast ? today : new Date(today.getFullYear() - PAST_YEARS, 0)}
              endMonth={new Date(today.getFullYear() + FUTURE_YEARS, 11)}
              disabled={restrictPast ? { before: today } : undefined}
              selected={selected}
              onSelect={handleSelect}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

const triggerStyle: React.CSSProperties = {
  fontSize: "0.875rem",
  padding: "6px 10px",
  border: "1px solid #ced4da",
  borderRadius: "4px",
  backgroundColor: "#fff",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const popoverStyle: React.CSSProperties = {
  position: "fixed",
  zIndex: 2000,
  backgroundColor: "#fff",
  border: "1px solid #ced4da",
  borderRadius: "6px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
  padding: "8px",
};

export default DateRangePicker;
